/* sugar:assets/sticky-custom.js@2.1.4 */
/**
 * Sticky Custom Web Component
 * Shared sticky behavior for custom desktop/mobile sticky bars.
 */
class StickyCustom extends HTMLElement {
  constructor() {
    super();

    this.config = {
      position: "bottom",
      visibility: "always",
      scrollPercentage: 10,
      anchorId: "",
      hideAnchorIds: [],
      docked: false,
      hideAtBottom: false,
      deviceType: "desktop",
    };

    this.scrollTicking = false;
    this.dockedScrollTicking = false;
    this.dockedOriginalTop = null;
    this.dockedHeight = 0;
    this.dockScrollThreshold = 0;
    this._lastPlaceholderDisplay = null;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.parseConfig();
    this.applyFullWidthActionSizing();
    this.setupEventListeners();
    this.setupAnalytics();
    this.watchPinnedBars();

    if (this.config.docked) {
      this.setupDocking();
    }

    this.updateVisibility();
    if ((this.config.visibility === "section" || this.config.visibility === "until") && document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.updateVisibility(), { once: true });
    }
    if (this.config.visibility === "between") {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.watchBetween(), { once: true });
      } else {
        this.watchBetween();
      }
      if (window.Shopify && window.Shopify.designMode) {
        // A section the editor re-renders is a new element; watch the new one.
        this._rewatch = () => this.watchBetween();
        document.addEventListener("shopify:section:load", this._rewatch);
      }
    }
    // Some nested blocks initialize after first paint.
    requestAnimationFrame(() => this.applyFullWidthActionSizing());
    setTimeout(() => this.applyFullWidthActionSizing(), 150);
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.teardownAnalytics();
    this.unwatchBetween();
    if (this._pinnedObserver) {
      this._pinnedObserver.disconnect();
      this._pinnedObserver = null;
    }
    if (this._rewatch) {
      document.removeEventListener("shopify:section:load", this._rewatch);
      this._rewatch = null;
    }

    if (this.dockedPlaceholder && this.dockedPlaceholder.parentNode) {
      this.dockedPlaceholder.parentNode.removeChild(this.dockedPlaceholder);
      this.dockedPlaceholder = null;
    }
  }

  /* ── Sugar Analytics ──────────────────────────────────────────────
   * The Custom Sticky counterpart to sticky-product.js's trackSticky: one
   * impression per section per session (whichever of the desktop/mobile bars
   * shows first), plus a click on the bar's buy buttons ('atc') and link
   * buttons ('link') so the dashboard can chart a click-through rate. The bar
   * carries no cart line, so revenue is attributed by the influenced-revenue
   * join (visitor clicked → purchased within 30 days) app-side.
   */

  setupAnalytics() {
    const entity = this.dataset.sectionId;
    if (!entity || this._analyticsBound) return;
    this._analyticsBound = true;
    this._impFired = false;

    // Impression fires the first time the bar is genuinely shown (see
    // checkImpression). onScroll/onResize re-check as it reveals; the delayed
    // check covers always-visible bars that never trigger a scroll, once their
    // reveal transition has settled.
    this.checkImpression();
    this._impTimer = setTimeout(() => this.checkImpression(), 600);

    // Buy/link buttons live in nested blocks, so the click is delegated from
    // the bar. Capture phase so it still runs if a nested handler stops
    // propagation (or navigates away).
    this._onStickyClick = (event) => {
      const target = event.target;
      if (!target || !target.closest) return;

      // Link buttons (link-button, simple-link-button, mini-link-button) are
      // anchors that navigate away.
      if (target.closest('a[href]')) {
        this.trackSticky('click', 'link');
        return;
      }
      // Quantity steppers are not a call-to-action.
      if (target.closest('.quantity__button, [name="minus"], [name="plus"]')) {
        return;
      }
      // Add-to-cart submit, ghost buy button, or accelerated checkout button.
      if (
        target.closest(
          'button[name="add"], .product-form__submit, [data-text-buy-button], .shopify-payment-button'
        )
      ) {
        this.trackSticky('click', 'atc');
      }
    };
    this.addEventListener('click', this._onStickyClick, true);
  }

  teardownAnalytics() {
    if (this._impTimer) {
      clearTimeout(this._impTimer);
      this._impTimer = null;
    }
    if (this._onStickyClick) {
      this.removeEventListener('click', this._onStickyClick, true);
      this._onStickyClick = null;
    }
  }

  /**
   * Fire one impression per section per session, the first time the bar is
   * genuinely visible: on screen, sized, and not in its opacity:0 hidden
   * state. IntersectionObserver can't be used here — a docked bottom +
   * scroll-% bar sits fixed at the viewport edge with opacity:0, so it
   * "intersects" while invisible, and an opacity change never re-triggers the
   * observer. Desktop + mobile bars share the section id, so whichever shows
   * first wins (session-deduped); the other no-ops.
   */
  checkImpression() {
    if (this._impFired) return;
    const entity = this.dataset.sectionId;
    if (!entity) return;

    const rect = this.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (rect.bottom <= 0 || rect.top >= vh) return;

    // getComputedStyle forces a reflow, so only reach for it once the cheap
    // geometry checks pass and the impression hasn't fired yet.
    let hidden = false;
    try {
      const style = getComputedStyle(this);
      hidden = style.opacity === '0' || style.visibility === 'hidden';
    } catch (e) {}
    if (hidden) return;

    this._impFired = true;
    let first = true;
    try {
      if (sessionStorage.getItem('_sugar_imp_' + entity)) first = false;
      else sessionStorage.setItem('_sugar_imp_' + entity, '1');
    } catch (e) {}
    if (first) this.trackSticky('impression', '');
  }

  trackSticky(type, detail) {
    const entity = this.dataset.sectionId;
    if (!entity) return;
    const body = document.body;
    const ctx = {
      template: (body && body.getAttribute('data-sugar-template')) || '',
      path: window.location.pathname,
      product: this.dataset.productHandle || '',
      productTitle: this.dataset.productTitle || '',
      label: '',
      section: entity
    };
    const args = [type, 'sticky', entity, detail || '', ctx];
    if (window.SugarAnalytics) window.SugarAnalytics.track.apply(null, args);
    else (window._sugarQ = window._sugarQ || []).push(args);
  }

  parseConfig() {
    this.config.position = this.dataset.position || "bottom";
    this.config.visibility = this.dataset.visibility || "always";
    this.config.scrollPercentage = parseInt(this.dataset.scrollPercentage, 10);
    if (Number.isNaN(this.config.scrollPercentage)) {
      this.config.scrollPercentage = 10;
    }
    this.config.anchorId = (this.dataset.anchorId || "").trim().replace(/^#/, "");
    this.config.hideAnchorIds = (this.dataset.hideAnchorIds || "")
      .split(",")
      .map((id) => id.trim().replace(/^#/, ""))
      .filter(Boolean);
    this.config.docked = this.dataset.docked === "true";
    this.config.hideAtBottom = this.dataset.hideAtBottom === "true";
    this.config.deviceType = this.dataset.deviceType || "desktop";
  }

  setupEventListeners() {
    // "After a section, hidden over others" is driven by IntersectionObserver alone; it only
    // needs the scroll listener to dock in place or to hide at the bottom of the page.
    if (this.config.visibility !== "between" || this.config.docked || this.config.hideAtBottom) {
      window.addEventListener("scroll", this.onScroll, { passive: true });
    }
    window.addEventListener("resize", this.onResize, { passive: true });
  }

  removeEventListeners() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
  }

  onScroll() {
    if (this.scrollTicking) return;

    this.scrollTicking = true;
    requestAnimationFrame(() => {
      if (this.config.docked) {
        this.updateDockedState();
      }
      this.updateVisibility();
      this.checkImpression();
      this.scrollTicking = false;
    });
  }

  onResize() {
    this.syncPinnedBars();
    if (this.config.docked) {
      this.refreshDockMetrics();
      this.updateDockedState();
    }
    this.updateVisibility();
    this.checkImpression();
  }

  /**
   * A top bar sits under every announcement bar pinned to the top of the window: one that
   * always sticks, or one that comes back on scroll up while it is showing. Their height
   * goes on this bar as --cs-pinned-bars, written only when it changes.
   */
  syncPinnedBars() {
    if (this.config.position !== "top") return;
    let height = 0;
    document.querySelectorAll(".sugar-announcement-bar--section").forEach((bar) => {
      if (bar.querySelector('[data-ab-sticky="always"]') || bar.getAttribute("data-ab-pinned") === "shown") {
        height += bar.offsetHeight;
      }
    });
    if (this.pinnedBarsHeight === height) return;
    this.pinnedBarsHeight = height;
    this.style.setProperty("--cs-pinned-bars", `${height}px`);
  }

  watchPinnedBars() {
    if (this.config.position !== "top") return;
    this.pinnedBarsHeight = null;
    this.syncPinnedBars();
    // Bars later in the page than this one are not parsed yet.
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.watchPinnedBars(), { once: true });
      return;
    }
    // An on-scroll-up bar pins and lifts as the visitor scrolls; its script marks each change.
    const scrollUpBars = [...document.querySelectorAll(".sugar-announcement-bar--section")]
      .filter((bar) => bar.querySelector('[data-ab-sticky="on-scroll-up"]'));
    if (!scrollUpBars.length || this._pinnedObserver) return;
    this._pinnedObserver = new MutationObserver(() => this.syncPinnedBars());
    scrollUpBars.forEach((bar) => this._pinnedObserver.observe(bar, { attributes: true, attributeFilter: ["data-ab-pinned"] }));
  }

  /**
   * Read --header-sticky-offset from the inline style on <html>, plus the pinned
   * announcement bars above a top bar.
   * This avoids getComputedStyle, which forces a synchronous reflow.
   */
  getHeaderStickyOffset() {
    const raw = document.documentElement.style.getPropertyValue(
      "--header-sticky-offset"
    );
    return (parseInt(raw, 10) || 0) + (this.pinnedBarsHeight || 0);
  }

  /**
   * Set placeholder display only when the value actually changes,
   * avoiding unnecessary DOM mutations that cause layout thrashing.
   */
  setPlaceholderDisplay(value) {
    if (this._lastPlaceholderDisplay === value) return;
    this._lastPlaceholderDisplay = value;
    this.dockedPlaceholder.style.display = value;
  }

  setupDocking() {
    this.dockedPlaceholder = document.createElement("div");
    this.dockedPlaceholder.style.cssText =
      "margin:0;padding:0;border:none;visibility:hidden;overflow-anchor:none;display:block;";

    if (this.nextSibling) {
      this.parentNode.insertBefore(this.dockedPlaceholder, this.nextSibling);
    } else {
      this.parentNode.appendChild(this.dockedPlaceholder);
    }

    this.refreshDockMetrics();

    if (this.config.position === "top") {
      this.setPlaceholderDisplay("none");
    } else {
      this.setPlaceholderDisplay("block");
    }

    this.updateDockedState();
  }

  refreshDockMetrics() {
    if (!this.dockedPlaceholder) return;

    const rect = this.getBoundingClientRect();
    this.dockedHeight = Math.max(Math.round(rect.height), this.offsetHeight || 0);
    this.dockedPlaceholder.style.height = `${this.dockedHeight}px`;

    if (this.config.position === "top" && !this.classList.contains("is-stuck")) {
      this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
    }
  }

  updateDockedState() {
    if (!this.config.docked || !this.dockedPlaceholder) return;

    if (this.config.position === "top") {
      this.updateTopDockState();
    } else {
      this.updateBottomDockState();
    }
  }

  updateTopDockState() {
    if (typeof this.dockedOriginalTop !== "number") {
      const rect = this.getBoundingClientRect();
      this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
    }

    const headerOffset = this.getHeaderStickyOffset();
    const stickThreshold = this.dockedOriginalTop - headerOffset;
    const buffer = 5;
    const isStuck = this.classList.contains("is-stuck");

    // For scroll % visibility, only stick when the percentage threshold is met.
    const canStick =
      this.config.visibility === "always" || this.triggerMet();

    if (!isStuck && window.scrollY >= stickThreshold && canStick) {
      this.classList.add("is-stuck");
      this.setPlaceholderDisplay("block");
    } else if (isStuck && (window.scrollY < stickThreshold - buffer || !canStick)) {
      this.classList.remove("is-stuck");
      this.setPlaceholderDisplay("none");
    }
  }

  updateBottomDockState() {
    const buffer = 8;

    if (!this.classList.contains("is-docked")) {
      const barVisualTop = window.innerHeight - this.dockedHeight;
      const placeholderRect = this.dockedPlaceholder.getBoundingClientRect();

      // Dock when the placeholder scrolls up to meet the fixed bar's position.
      if (placeholderRect.top <= barVisualTop) {
        this.dockScrollThreshold = window.scrollY;
        this.classList.add("is-docked");
        this.setPlaceholderDisplay("none");
      }
    } else if (window.scrollY < this.dockScrollThreshold - buffer) {
      this.classList.remove("is-docked");
      this.setPlaceholderDisplay("block");
    }
  }

  // Class writes only happen when the state changes, so a scroll frame that changes
  // nothing restyles nothing.
  setClass(name, on) {
    if (this.classList.contains(name) !== on) this.classList.toggle(name, on);
  }

  updateVisibility() {
    const hideNearBottom = this.shouldHideAtBottom();

    this.setClass("is-hidden-at-bottom", hideNearBottom);

    if (this.config.docked) {
      // Bottom dock + scroll %: control visibility of the fixed (non-docked) state.
      // When docked inline, the is-docked CSS rule ensures full opacity.
      // Top dock visibility is handled by updateTopDockState (only sticks when scroll % met).
      if (this.config.position === "bottom" && this.config.visibility !== "always") {
        let shouldShow = this.triggerMet();
        if (hideNearBottom) shouldShow = false;
        this.setClass("is-visible", shouldShow);
      }
      return;
    }

    let shouldShow = this.config.visibility === "always";

    if (this.config.visibility !== "always") {
      shouldShow = this.triggerMet();
    }

    if (hideNearBottom) {
      shouldShow = false;
    }

    this.setClass("is-visible", shouldShow);
  }

  shouldHideAtBottom() {
    if (!this.config.hideAtBottom) return false;

    const threshold = 120;
    const scrollBottom = window.scrollY + window.innerHeight;
    const pageBottom = document.documentElement.scrollHeight - threshold;
    return scrollBottom >= pageBottom;
  }

  // "After scrolling" passes once the page is scrolled far enough; "After a section" passes
  // while the element with the anchor ID is less than 80% on screen (80% of the screen
  // height for one taller than the screen), so the bar hides while that section is in view.
  // "Until a section" passes from the visitor's first scroll until the element with the
  // anchor ID reaches the screen, so the bar never covers that section or anything below it.
  // "After a section, hidden over others" passes once the anchor's bottom has risen above 40%
  // of the screen, except while any section in the hide list is on screen (watchBetween).
  triggerMet() {
    if (this.config.visibility === "between") {
      return this._betweenShown === true;
    }
    if (this.config.visibility === "viewport") {
      return window.scrollY >= window.innerHeight * this.config.scrollPercentage / 100;
    }
    if (this.config.visibility === "until") {
      if (!this.hasScrolled) {
        if (window.scrollY <= 0) return false;
        this.hasScrolled = true;
      }
      const target = this.config.anchorId ? document.getElementById(this.config.anchorId) : null;
      if (!target) return true;
      return target.getBoundingClientRect().top >= window.innerHeight;
    }
    if (this.config.visibility === "section") {
      const target = this.config.anchorId ? document.getElementById(this.config.anchorId) : null;
      if (!target) return document.readyState !== "loading";
      const rect = target.getBoundingClientRect();
      const vh = window.innerHeight;
      const visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
      const full = Math.min(rect.height, vh);
      return full <= 0 || visible / full < 0.8;
    }
    if (this.config.visibility === "custom_stay") {
      // Latches: once the bar has appeared it keeps showing, also when the visitor scrolls back up.
      if (!this._stayShown && this.getScrollPercentage() >= this.config.scrollPercentage) this._stayShown = true;
      return this._stayShown === true;
    }
    return this.getScrollPercentage() >= this.config.scrollPercentage;
  }

  /* ── After a section, hidden over others ───────────────────────────
   * Two IntersectionObservers keep the state, so nothing is measured on scroll. Each
   * callback reads only its own entries, and the bar is written once, on the next frame,
   * and only when the shown state flips.
   *
   * The trigger is observed against a root that runs from far above the page down to 40%
   * of the screen. Its bottom crossing that line moves the intersection ratio across 1,
   * and a jump past it (an anchor link) moves it across 0 or 1 as well, so the passed
   * state is always reported. The root is the document so the root margin still applies
   * inside the theme editor's frame.
   */
  createObserver(callback, rootMargin, threshold) {
    try {
      return new IntersectionObserver(callback, { root: document, rootMargin, threshold });
    } catch (e) {
      return new IntersectionObserver(callback, { rootMargin, threshold });
    }
  }

  // "footer" also matches the store or page footer when no element carries that id.
  resolveHideTargets() {
    const found = new Set();
    this.config.hideAnchorIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        found.add(el);
      } else if (id.toLowerCase() === "footer") {
        document
          .querySelectorAll(".shopify-section-group-footer-group, .shopify-section.footer-section")
          .forEach((section) => found.add(section));
      }
    });
    return [...found];
  }

  watchBetween() {
    this.unwatchBetween();
    if (!this.isConnected) return;

    const trigger = this.config.anchorId ? document.getElementById(this.config.anchorId) : null;
    this._betweenPassed = !trigger;
    this._betweenOver = new Set();

    if (trigger) {
      this._triggerObserver = this.createObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          const line = entry.rootBounds ? entry.rootBounds.bottom : window.innerHeight * 0.4;
          this._betweenPassed = entry.boundingClientRect.bottom <= line + 1;
          this.syncBetween();
        },
        "100000px 0px -60% 0px",
        [0, 1]
      );
      this._triggerObserver.observe(trigger);
    }

    const hideTargets = this.resolveHideTargets();
    if (hideTargets.length) {
      this._hideObserver = this.createObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this._betweenOver.add(entry.target);
            else this._betweenOver.delete(entry.target);
          });
          this.syncBetween();
        },
        "0px",
        0
      );
      hideTargets.forEach((el) => this._hideObserver.observe(el));
    }

    // Each observer reports once as soon as it starts, so the first state comes from them;
    // with nothing to watch the bar simply shows.
    if (!trigger && !hideTargets.length) this.syncBetween();
  }

  unwatchBetween() {
    if (this._triggerObserver) this._triggerObserver.disconnect();
    if (this._hideObserver) this._hideObserver.disconnect();
    this._triggerObserver = null;
    this._hideObserver = null;
  }

  syncBetween() {
    const shown = this._betweenPassed && this._betweenOver.size === 0;
    if (shown === this._betweenShown) return;
    this._betweenShown = shown;
    // One coalesced update per frame, shared with the scroll path when that is attached.
    this.onScroll();
    // The reveal is a transition, so the impression check waits for it to settle.
    if (shown && !this._impFired) setTimeout(() => this.checkImpression(), 350);
  }

  getScrollPercentage() {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const maxScroll = doc.scrollHeight - window.innerHeight;

    if (maxScroll <= 0) return 100;
    return (scrollTop / maxScroll) * 100;
  }

  applyFullWidthActionSizing() {
    const content = this.querySelector(".custom-sticky__content");
    if (!content) return;

    // Center middle is a three-slot grid; its cells already size themselves.
    if ((content.dataset.layout || "flex") === "center-middle") {
      return;
    }

    const fullWidthButtons = content.querySelectorAll(
      ".button--full-width"
    );
    if (!fullWidthButtons.length) return;

    fullWidthButtons.forEach((button) => {
      const actionWrapper = button.closest(
        ".sugar-link-button, .sugar-product-buy-button"
      );
      if (!actionWrapper) return;

      // Flex item is usually Shopify's generated block wrapper.
      const generatedBlockWrapper = actionWrapper.closest("[id^='shopify-block-']");
      const flexItem =
        generatedBlockWrapper && content.contains(generatedBlockWrapper)
          ? generatedBlockWrapper
          : actionWrapper;

      flexItem.style.width = "100%";
      flexItem.style.maxWidth = "100%";
      flexItem.style.flex = "1 1 100%";
      flexItem.style.minWidth = "0";

      actionWrapper.style.width = "100%";
      actionWrapper.style.maxWidth = "100%";
      actionWrapper.style.flex = "1 1 100%";
    });
  }
}

if (!customElements.get("sticky-custom")) {
  customElements.define("sticky-custom", StickyCustom);
}

/* ── Page variant bridge ────────────────────────────────────────────
 * A Custom Sticky bar is its own section, and the shared price store only answers
 * to events stamped with the section it belongs to. That is what lets a bar with its
 * own variant picker price itself independently of the page. A bar without one has
 * nothing to move it: it would hold the first variant's price while the page showed
 * another, and add that first variant to the cart. So when the bar selects nothing
 * itself, re-stamp the page's selection with the bar's id and let the bar follow.
 */
(function () {
  // A picker of either kind makes the bar self-sufficient.
  const PICKERS = ".sugar-product-variant-picker, [data-ov]";

  // Offer tiers are the subtle case: every tier carries the current variant, but a plain
  // tier is choosing a quantity, not a variant, and the bar should still follow the page.
  // Only a tier locked to one variant, or selling another product, takes the variant for
  // itself — the same two conditions offers.js uses before it writes the form.
  function ownsSelection(sectionEl) {
    if (sectionEl.querySelector(PICKERS)) return true;
    return Array.prototype.some.call(
      sectionEl.querySelectorAll("[data-sugar-offers] .offer"),
      (offer) => {
        if (offer.dataset.variantLocked === "true") return true;
        const bar = offer.closest("sticky-custom");
        const handle = offer.dataset.productHandle;
        return !!(bar && handle && handle !== bar.dataset.productHandle);
      }
    );
  }

  // Resolved on every event, never captured. The editor re-renders a section by
  // replacing the element, and a held reference goes on answering for a detached bar.
  function stickySectionIds() {
    const ids = new Set();
    document.querySelectorAll("sticky-custom[data-section-id]").forEach((bar) => {
      if (bar.dataset.sectionId) ids.add(bar.dataset.sectionId);
    });
    return ids;
  }

  // The variants the bar's own product has. A page variant outside this set belongs to
  // a different product — the bar's Custom product — and the bar must not follow it.
  function ownVariantIds(sectionEl) {
    const ids = new Set();
    sectionEl.querySelectorAll("sticky-custom[data-variant-ids]").forEach((bar) => {
      bar.dataset.variantIds
        .split(",")
        .forEach((id) => {
          const trimmed = id.trim();
          if (trimmed) ids.add(trimmed);
        });
    });
    return ids;
  }

  function follow(sectionEl, variant, ownIds) {
    const available = variant.available !== false;

    sectionEl.querySelectorAll("input[name='id'].product-variant-id").forEach((input) => {
      // Scoped by the bar's own variants, so an upsell or a product card sitting in the
      // bar keeps the variant it was rendered with.
      if (!ownIds.has(String(input.value))) return;
      input.value = variant.id;
      input.disabled = !available;
      input.dispatchEvent(new Event("change", { bubbles: true }));

      const form = input.closest("form");
      const submit = form && form.querySelector(".product-form__submit");
      if (!submit) return;
      // The page's own button is re-rendered by the Section Rendering API, which never
      // reaches this section, so availability has to be applied by hand or the bar would
      // go on offering a variant that is gone.
      submit.disabled = !available;
      submit.classList.toggle("is-sold-out", !available);
    });

    document.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: { sectionId: sectionEl.id.replace(/^shopify-section-/, ""), variant, source: "sticky-bridge" },
      })
    );
  }

  document.addEventListener("variant:change", (e) => {
    const detail = e.detail || {};
    // Our own re-stamp, or an event already unscoped — every store hears that one.
    if (!detail.variant || detail.source === "sticky-bridge" || !detail.sectionId) return;
    const sourceId = String(detail.sectionId);

    stickySectionIds().forEach((stickyId) => {
      if (stickyId === sourceId) return;

      const sectionEl = document.getElementById("shopify-section-" + stickyId);
      if (!sectionEl || ownsSelection(sectionEl)) return;

      const ownIds = ownVariantIds(sectionEl);
      if (!ownIds.has(String(detail.variant.id))) return;

      follow(sectionEl, detail.variant, ownIds);
    });
  });
})();
